import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEARCH_QUERY_KEY, toQueryParam } from '../components/table-filters/queryCodec';
import { TableFilterState } from '../components/table-filters/types';

interface UseTableFilterStateOptions {
    replace?: boolean;
}

export function useTableFilterState<T extends TableFilterState>(
    defaults: T,
    options?: UseTableFilterStateOptions
) {
    const defaultsRef = useRef(defaults);
    const [searchParams, setSearchParams] = useSearchParams();
    const replace = options?.replace ?? true;
    const keys = useMemo(() => Object.keys(defaultsRef.current), []);

    const state = useMemo(() => {
        const next: Record<string, string> = {};
        for (const key of keys) {
            const queryKey = toQueryParam(key);
            const value = searchParams.get(queryKey);
            next[key] = value ?? defaultsRef.current[key];
        }
        return next as T;
    }, [keys, searchParams]);

    const writeState = (nextState: T) => {
        const nextParams = new URLSearchParams(searchParams);
        for (const key of keys) {
            const queryKey = toQueryParam(key);
            nextParams.delete(queryKey);
            const value = String(nextState[key] ?? '');
            const defaultValue = String(defaultsRef.current[key] ?? '');
            if (value && value !== defaultValue) {
                nextParams.set(queryKey, value);
            }
        }
        setSearchParams(nextParams, { replace });
    };

    const setFilter = <K extends keyof T>(key: K, value: string) => {
        const nextState = { ...state, [key]: value } as T;
        writeState(nextState);
    };

    const setFilters = (updates: Partial<T>) => {
        const nextState = { ...state, ...updates } as T;
        writeState(nextState);
    };

    const clearAll = () => {
        writeState(defaultsRef.current);
    };

    const isDirty = keys.some((key) => String(state[key] ?? '') !== String(defaultsRef.current[key] ?? ''));
    const queryObject = useMemo(() => {
        const query: Record<string, string> = {};
        for (const key of keys) {
            const value = String(state[key] ?? '');
            if (!value) continue;
            if (key === SEARCH_QUERY_KEY) query[SEARCH_QUERY_KEY] = value;
            else query[`f_${key}`] = value;
        }
        return query;
    }, [keys, state]);

    return { state, setFilter, setFilters, clearAll, isDirty, queryObject };
}
